import React, { useEffect, useRef, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';

/* ------- STYLES ------- */
const StyledGalleryGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const StyledGalleryItem = styled.li`
  cursor: pointer;

  .cover-image {
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    transition: var(--transition);
    margin-bottom: 15px;

    &:hover {
      transform: scale(1.02);
    }
  }

  h3 {
    margin: 0;
  }
`;

const StyledModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledModalContent = styled.div`
  position: relative;
  background-color: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 20px;
  max-width: 800px;
  width: 90%;
  box-shadow: var(--shadow);

  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    color: black;
    font-size: 1.5rem;
    cursor: pointer;
  }

  .slides {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 20px;

    .nav-button {
      background: none;
      border: none;
      color: black;
      font-size: 2rem;
      cursor: pointer;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);

      &:hover {
        color: var(--green);
      }
    }
    .prev {
      left: 10px;
    }
    .next {
      right: 10px;
    }

    .image-wrapper {
      width: 100%;
      max-height: 500px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        border-radius: var(--border-radius);
        max-height: 500px;
        object-fit: cover;
      }
    }
  }
`;

/* ------- BUTTON STYLE ------- */
const StyledHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; /* Title on left, button on right */
  margin-bottom: 30px;

  .portfolio-button {
    ${({ theme }) => theme.mixins.smallButton};
    font-size: var(--fz-xs);
    text-decoration: none;
    margin-left: 15px;
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        # Sort by 'priority' DESC, then by 'date' ASC
        sort: { fields: [frontmatter___priority, frontmatter___date], order: [ASC, ASC] }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              priority
              gallery {
                childImageSharp {
                  gatsbyImageData(width: 1000)
                }
              }
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.map(({ node }) => {
    const { title, tech, priority, gallery } = node.frontmatter;
    return {
      title,
      tech,
      // Fallback to 0 if priority is missing
      priority: priority || 0,
      description: node.html,
      gallery: gallery?.map((img) => getImage(img)) || [],
    };
  });

  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion]);

  // Modal Handlers
  const openModal = project => {
    setCurrentProject(project);
    setCurrentIndex(0);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentProject(null);
  };

  const nextImage = () => {
    if (!currentProject) return;
    setCurrentIndex(prev => (prev + 1) % currentProject.gallery.length);
  };
  const prevImage = () => {
    if (!currentProject) return;
    setCurrentIndex(prev =>
      prev === 0 ? currentProject.gallery.length - 1 : prev - 1
    );
  };

  return (
    <section id="projects">
      {/* Title & Button Row */}
      <StyledHeadingWrapper>
        <h2 className="numbered-heading">Recent Work</h2>
        <a
          className="portfolio-button"
          href="/portfolio.pdf"
          target="_blank"
          rel="noopener noreferrer">
          Portfolio
        </a>
      </StyledHeadingWrapper>

      <StyledGalleryGrid>
        {featuredProjects.map((project, i) => (
          <StyledGalleryItem
            key={i}
            ref={el => (revealProjects.current[i] = el)}
            onClick={() => openModal(project)}>
            {project.gallery[0] && (
              <GatsbyImage
                image={project.gallery[0]}
                alt={project.title}
                className="cover-image"
              />
            )}
            <h3>{project.title}</h3>
          </StyledGalleryItem>
        ))}
      </StyledGalleryGrid>

      {isModalOpen && currentProject && (
        <StyledModalOverlay onClick={closeModal}>
          <StyledModalContent onClick={e => e.stopPropagation()}>
            <button className="close-button" onClick={closeModal}>
              &times;
            </button>

            {/* Slideshow */}
            {currentProject.gallery.length > 0 && (
              <div className="slides">
                <button className="nav-button prev" onClick={prevImage}>
                  &lt;
                </button>
                <div className="image-wrapper">
                  <GatsbyImage
                    image={currentProject.gallery[currentIndex]}
                    alt={currentProject.title}
                  />
                </div>
                <button className="nav-button next" onClick={nextImage}>
                  &gt;
                </button>
              </div>
            )}

            {/* Project Info */}
            <h3>{currentProject.title}</h3>
            <div
              dangerouslySetInnerHTML={{ __html: currentProject.description }}
            />
            {currentProject.tech?.length > 0 && (
              <ul>
                {currentProject.tech.map((t, idx) => (
                  <li key={idx}>{t}</li>
                ))}
              </ul>
            )}
          </StyledModalContent>
        </StyledModalOverlay>
      )}
    </section>
  );
};

export default Featured;
